'use client';

import { Suspense, useEffect, useMemo, useState } from 'react';

import { useSearchParams } from 'next/navigation';

import {
  ArrowUpRight,
  AtSign,
  BadgeCheck,
  BookOpen,
  Crosshair,
  HeartPulse,
  Languages,
  Layers,
  Lightbulb,
  type LucideIcon,
  Mail,
  MessageCircle,
  Network,
  Phone,
  PhoneCall,
  PhoneOutgoing,
  Route,
  Search,
  SearchCheck,
  Settings2,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Workflow,
} from 'lucide-react';

import Logos from '@/components/sections/logos';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Link } from '@/lib/i18n/link';
import {
  USE_CASES,
  USE_CASE_PRODUCTS,
  type UseCase,
  type UseCaseChannel,
  type UseCasePhase,
  type UseCaseProduct,
} from '@/lib/use-cases-data';
import { cn } from '@/lib/utils';

/* ------------------------------ Constants ------------------------------ */

/** One thin-stroke icon per use-case family, rendered as a quiet tile. */
const FAMILY_ICONS: Record<string, LucideIcon> = {
  'AI Layer on top of Existing Helpdesk': Layers,
  'Smart Routing': Route,
  'Assist Mode': Sparkles,
  'Multi-Language': Languages,
  'Knowledge Management': BookOpen,
  'AI Voice Support': Phone,
  'AI Social Support': AtSign,
  'AI Email Support': Mail,
  Security: ShieldCheck,
  'AI Chat Support': MessageCircle,
  'Sales Outreach': PhoneOutgoing,
  'AI Voice Outbound': PhoneCall,
  'AI Outbound Sequences': Workflow,
  'Root Cause Analysis': SearchCheck,
  'QA & Performance': BadgeCheck,
  'Product Intelligence': Lightbulb,
  'Customer Sentiment': HeartPulse,
  'Competitive Intelligence': Crosshair,
  'Complex Communication': Network,
  'Revenue Workflows': TrendingUp,
  'Operations Automation': Settings2,
};

/** Filterable channels, in display order. "All channels" rows match any. */
const CHANNEL_FILTERS: UseCaseChannel[] = [
  'Voice',
  'Chat',
  'Email',
  'WhatsApp',
  'SMS',
  'Instagram',
  'Messenger',
  'Social',
];

const PHASE_FILTERS: UseCasePhase[] = ['Pre-sale', 'Sale', 'Post-sale'];

const PRODUCT_BLURBS: Record<UseCaseProduct, string> = {
  Inbound:
    'The front line: AI resolving the conversations your customers start, on every channel.',
  Outbound:
    'Proactive by default: AI reaching out for sales, retention, and support before customers have to ask.',
  'Customer Insights':
    'Every conversation, turned into product and CX intelligence your teams act on.',
  'Agentic Workflows':
    'Multi-step, multi-party processes running end-to-end with no human coordination.',
};

const matchesChannel = (uc: UseCase, channel: UseCaseChannel) =>
  uc.channels.includes('All channels') || uc.channels.includes(channel);

/* -------------------------------- Card --------------------------------- */

const LiveBadge = () => (
  <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[10px] font-medium tracking-wide text-emerald-700 uppercase">
    <span className="relative flex h-1.5 w-1.5">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
    </span>
    Live
  </span>
);

const UseCaseCard = ({ useCase }: { useCase: UseCase }) => {
  const FamilyIcon = FAMILY_ICONS[useCase.family];
  return (
    <article className="group relative flex h-full flex-col rounded-2xl border border-neutral-200 bg-white p-6 transition-colors duration-200 hover:border-neutral-300 md:p-7">
      {/* Header row: family icon + live badge */}
      <div className="mb-5 flex items-start justify-between gap-3">
        {FamilyIcon ? (
          <span
            aria-hidden
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-neutral-200 bg-[#fafaf9] text-neutral-700"
          >
            <FamilyIcon className="h-4 w-4" strokeWidth={1.75} />
          </span>
        ) : (
          <span aria-hidden className="h-9" />
        )}
        <LiveBadge />
      </div>

      <p className="mb-2 text-[10px] font-medium tracking-[0.16em] text-neutral-500 uppercase">
        {useCase.family}
      </p>
      <h3 className="font-sans text-base text-neutral-900 md:text-lg">
        {useCase.name}
      </h3>
      <p className="mt-2 text-sm leading-relaxed text-neutral-500 md:text-[15px]">
        {useCase.description}
      </p>

      {/* Impact chips */}
      <div className="mt-5 mb-6 flex flex-wrap gap-1.5">
        {useCase.impact.map((item) => (
          <span
            key={item}
            className="rounded-full bg-[#f3f1ec] px-2.5 py-1 text-xs font-medium text-neutral-700"
          >
            {item}
          </span>
        ))}
      </div>

      {/* Footer: channels + journey phase */}
      <div className="mt-auto flex flex-wrap items-center justify-between gap-3 border-t border-neutral-200 pt-4">
        <span className="text-xs text-neutral-500">
          {useCase.channels.join(' · ')}
        </span>
        <div className="flex items-center gap-1.5">
          {useCase.phases.map((phase) => (
            <span
              key={phase}
              className="rounded-full border border-neutral-200 px-2 py-0.5 text-[10px] font-medium tracking-wide text-neutral-500 uppercase"
            >
              {phase}
            </span>
          ))}
        </div>
      </div>
    </article>
  );
};

/* -------------------------------- Page --------------------------------- */

/**
 * Reads `?product=` and lifts it into the page's filter state. Renders nothing.
 *
 * Isolated behind its own Suspense boundary deliberately. `useSearchParams()`
 * opts the nearest boundary out of static rendering, and the whole page used
 * to sit inside one `<Suspense>` with no fallback — so the server emitted the
 * fallback, which was nothing. /use-cases shipped 180 words of chrome with no
 * `<h1>` and not one of its use cases in the HTML; everything appeared only
 * after hydration, which a crawler does not wait for. Confining the hook to a
 * component that renders null keeps the bail-out to null.
 */
function ProductParam({
  onProduct,
}: {
  onProduct: (product: UseCaseProduct) => void;
}) {
  const searchParams = useSearchParams();

  useEffect(() => {
    const product = searchParams.get('product');
    if (product && USE_CASE_PRODUCTS.includes(product as UseCaseProduct)) {
      onProduct(product as UseCaseProduct);
    }
  }, [searchParams, onProduct]);

  return null;
}

function UseCasesContent() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<UseCaseProduct | null>(
    null,
  );
  const [selectedChannel, setSelectedChannel] = useState<UseCaseChannel | null>(
    null,
  );
  const [selectedPhase, setSelectedPhase] = useState<UseCasePhase | null>(null);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return USE_CASES.filter((uc) => {
      const matchesSearch = q
        ? [uc.name, uc.family, uc.description, ...uc.impact]
            .join(' ')
            .toLowerCase()
            .includes(q)
        : true;
      const matchesProduct = selectedProduct
        ? uc.product === selectedProduct
        : true;
      const matchesCh = selectedChannel
        ? matchesChannel(uc, selectedChannel)
        : true;
      const matchesPhase = selectedPhase
        ? uc.phases.includes(selectedPhase)
        : true;
      return matchesSearch && matchesProduct && matchesCh && matchesPhase;
    });
  }, [searchQuery, selectedProduct, selectedChannel, selectedPhase]);

  const pillClass = (active: boolean) =>
    cn(
      'inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors duration-200 md:text-sm',
      active
        ? 'border-neutral-900 bg-neutral-900 text-white'
        : 'border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300 hover:text-neutral-900',
    );

  return (
    <>
      <Suspense fallback={null}>
        <ProductParam onProduct={setSelectedProduct} />
      </Suspense>
      {/* Hero */}
      <section className="bg-[#fafaf9] pt-28 pb-14 md:pt-36 md:pb-16">
        <div className="container">
          <div className="mx-auto max-w-4xl text-center">
            <p className="mb-4 inline-flex items-center gap-2 text-[11px] font-medium tracking-[0.16em] text-neutral-500 uppercase">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
              </span>
              Live use cases
            </p>
            <h1 className="font-serif text-[2rem] text-balance text-neutral-900 md:text-4xl xl:text-5xl">
              Running in production, right now
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-neutral-500 md:text-lg">
              {USE_CASES.length} use cases our customers run live on Open today,
              across chat, voice, email, WhatsApp, and social. Search them,
              filter them, and steal them for your own stack.
            </p>

            {/* Search */}
            <div className="mx-auto mt-10 max-w-3xl">
              <div className="relative">
                <Search className="absolute top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400 ltr:left-4 rtl:right-4" />
                <Input
                  type="text"
                  placeholder="Search use cases, e.g. reminders, churn, routing, QA..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-12 rounded-full border-neutral-200 bg-white text-sm shadow-none focus-visible:border-neutral-900 focus-visible:ring-1 focus-visible:ring-neutral-900 md:text-base ltr:pr-4 ltr:pl-11 rtl:pr-11 rtl:pl-4"
                />
              </div>

              {/* Product filter */}
              <div className="mt-8 flex flex-wrap justify-center gap-2">
                {USE_CASE_PRODUCTS.map((product) => (
                  <button
                    key={product}
                    onClick={() =>
                      setSelectedProduct(
                        selectedProduct === product ? null : product,
                      )
                    }
                    className={pillClass(selectedProduct === product)}
                  >
                    {product}
                  </button>
                ))}
              </div>

              {/* Channel + phase filters */}
              <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
                {CHANNEL_FILTERS.map((channel) => (
                  <button
                    key={channel}
                    onClick={() =>
                      setSelectedChannel(
                        selectedChannel === channel ? null : channel,
                      )
                    }
                    className={pillClass(selectedChannel === channel)}
                  >
                    {channel}
                  </button>
                ))}
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
                {PHASE_FILTERS.map((phase) => (
                  <button
                    key={phase}
                    onClick={() =>
                      setSelectedPhase(selectedPhase === phase ? null : phase)
                    }
                    className={pillClass(selectedPhase === phase)}
                  >
                    {phase}
                  </button>
                ))}
              </div>

              <p className="mt-6 text-xs text-neutral-500">
                Showing {filtered.length} of {USE_CASES.length} live use cases
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Social proof: customer logos */}
      <div className="bg-[#fafaf9]">
        <Logos variant="cards" backgroundColor="transparent" hideEyebrow />
      </div>

      {/* Directory sections, grouped by product */}
      {USE_CASE_PRODUCTS.map((product, sectionIndex) => {
        const sectionCases = filtered.filter((uc) => uc.product === product);
        if (sectionCases.length === 0) return null;

        return (
          <section
            key={product}
            className={cn(
              'py-16 md:py-20',
              sectionIndex % 2 === 0 ? 'bg-white' : 'bg-[#fafaf9]',
            )}
          >
            <div className="container">
              <div className="mb-10 flex max-w-3xl flex-col md:mb-12">
                <p className="mb-3 text-[10px] font-medium tracking-[0.18em] text-neutral-500 uppercase">
                  {String(sectionIndex + 1).padStart(2, '0')} · {product}
                </p>
                <h2 className="font-serif text-2xl text-balance text-neutral-900 md:text-4xl">
                  {product}
                </h2>
                <p className="mt-3 text-base leading-relaxed text-neutral-500 md:text-lg">
                  {PRODUCT_BLURBS[product]}
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
                {sectionCases.map((uc) => (
                  <UseCaseCard key={uc.id} useCase={uc} />
                ))}
              </div>
            </div>
          </section>
        );
      })}

      {/* Empty state */}
      {filtered.length === 0 && (
        <section className="bg-white py-20">
          <div className="container">
            <div className="py-16 text-center">
              <p className="text-base leading-relaxed text-neutral-500 md:text-lg">
                No use cases match that search. Try different keywords or clear
                a filter.
              </p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedProduct(null);
                  setSelectedChannel(null);
                  setSelectedPhase(null);
                }}
                className="mt-6 inline-flex items-center rounded-full border border-neutral-900 px-5 py-2 text-sm font-medium text-neutral-900 transition-colors hover:bg-neutral-900 hover:text-white"
              >
                Clear all filters
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Closing CTA */}
      <section className="border-t border-neutral-200 bg-[#fafaf9] py-16 md:py-24">
        <div className="container">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-serif text-2xl text-balance text-neutral-900 md:text-4xl">
              Want one of these live for your customers?
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-neutral-500 md:text-lg">
              Most use cases go live in days, not quarters, on top of the
              helpdesk and phone system you already run.
            </p>
            <Button
              asChild
              className="mt-8 h-12 bg-neutral-900 px-7 text-sm font-medium text-white hover:bg-neutral-800"
            >
              <Link
                href="/demo"
                prefetch
                aria-label="Learn more about Open and book a demo"
              >
                Get a demo
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}

export default function UseCasesClient() {
  return <UseCasesContent />;
}
